import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChefHat, Loader2, Plus, Trash2 } from 'lucide-react'
import apiClient from '@/api/client'
import type { StockItem } from '@/types'
import type { PrepIngredientOption, PrepRecipe } from '@/types/kitchenFlowPrep'
import { useCurrency } from '@/contexts/CurrencyContext'
import { useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { allUnitOptions, getCompatibleUnits } from '@/lib/inventory-units'
import { cn } from '@/lib/utils'
import type { StockItemRef } from '@/lib/recipeEditor'
import { IngredientPicker } from '@/components/admin/costing/IngredientPicker'
import {
  canProduce,
  draftFromRecipe,
  draftProblems,
  newPrepLine,
  plainError,
  previewCost,
  sameDraft,
  toPayload,
  type PrepDraft,
  type PrepLineDraft,
} from './prepRecipeDraft'
import { PREP_TEXT } from './prepStrings'
import { PrepProduction } from './PrepProduction'
import { RecipeStepsEditor } from '@/components/recipe-card/RecipeStepsEditor'
import { RECIPE_CARD_QK } from '@/hooks/useRecipeCards'

export const PREP_RECIPE_QK = 'prep-recipe'
export const PREP_OPTIONS_QK = ['prep-ingredient-options'] as const

/**
 * "Made in our kitchen" — the prep-item part of the item drawer (kitchen flow
 * lane B). Renders nothing unless the tenant has FEATURE_KITCHEN_FLOW, so the
 * drawer is unchanged everywhere else.
 */
export function PrepSection({ item, showToast }: {
  item: StockItem
  showToast: (t: 'success' | 'error', m: string) => void
}) {
  const enabled = useKitchenFlowEnabled()
  if (!enabled) return null
  return <PrepSectionBody item={item} showToast={showToast} />
}

function unitLabel(u: string) {
  return allUnitOptions.find((o) => o.value === u)?.label ?? u
}

function PrepSectionBody({ item, showToast }: {
  item: StockItem
  showToast: (t: 'success' | 'error', m: string) => void
}) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()

  const { data: recipe, isLoading } = useQuery({
    queryKey: [PREP_RECIPE_QK, item.id],
    queryFn: () => apiClient.getPrepRecipe(item.id).then((r) => r.data as PrepRecipe),
  })
  const saved = useMemo(() => draftFromRecipe(recipe, item.unit), [recipe, item.unit])
  const [draft, setDraft] = useState<PrepDraft>(saved)
  const [serverError, setServerError] = useState<string | null>(null)
  // Adopt the saved recipe whenever it (re)loads.
  useEffect(() => {
    setDraft(saved)
    setServerError(null)
  }, [saved])

  const { data: options = [] } = useQuery({
    queryKey: PREP_OPTIONS_QK,
    queryFn: () => apiClient.getPrepIngredientOptions().then((r) => (r.data ?? []) as PrepIngredientOption[]),
    enabled: draft.isPrep,
    staleTime: 30_000,
  })
  const byId = useMemo(() => Object.fromEntries(options.map((o) => [o.id, o])), [options])
  // The picker's view: every item but this one, prep items marked.
  const pickList: StockItemRef[] = useMemo(
    () =>
      options
        .filter((o) => o.id !== item.id)
        .map((o) => ({ id: o.id, name: o.is_prep ? `${o.name} (${PREP_TEXT.madeInKitchen})` : o.name, unit: o.unit, unitCost: o.unit_cost })),
    [options, item.id],
  )

  const dirty = !sameDraft(draft, saved)
  const problems = draftProblems(draft, item.id, item.unit, byId)
  const rowProblems = new Map<string, string>()
  for (const p of problems) if (p.key && !rowProblems.has(p.key)) rowProblems.set(p.key, p.message)
  const cost = previewCost(draft, item.unit, byId)

  const save = useMutation({
    mutationFn: () => apiClient.putPrepRecipe(item.id, toPayload(draft)),
    onSuccess: (res) => {
      qc.setQueryData([PREP_RECIPE_QK, item.id], res.data)
      qc.invalidateQueries({ queryKey: PREP_OPTIONS_QK })
      qc.invalidateQueries({ queryKey: [PREP_RECIPE_QK] })
      // The kitchen's cards and the list of what has one are now out of date.
      qc.invalidateQueries({ queryKey: [RECIPE_CARD_QK] })
      setServerError(null)
      showToast('success', res.data?.is_prep ? PREP_TEXT.saved : PREP_TEXT.turnedOff)
    },
    onError: (e) => setServerError(plainError(e)),
  })

  const setLine = (key: string, patch: Partial<PrepLineDraft>) =>
    setDraft((d) => ({
      ...d,
      lines: d.lines.map((l) => {
        if (l.key !== key) return l
        const next = { ...l, ...patch }
        // A new ingredient starts in its own stock unit — batch recipes are
        // written in kilos and litres, not grams.
        if (patch.stock_item_id && patch.stock_item_id !== l.stock_item_id) next.unit = byId[patch.stock_item_id]?.unit ?? ''
        return next
      }),
    }))

  if (isLoading) return null

  return (
    <div className="rounded-lg border p-4 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="flex items-center gap-2 text-base font-semibold">
            <ChefHat className="h-4 w-4" aria-hidden /> {PREP_TEXT.sectionTitle}
          </h4>
          <p className="mt-0.5 text-sm text-muted-foreground">{PREP_TEXT.toggleHelp}</p>
        </div>
      </div>

      <label className="flex min-h-10 cursor-pointer items-center justify-between gap-3 rounded-md border px-3 py-2">
        <span className="text-sm font-medium">{PREP_TEXT.toggleLabel}</span>
        <Switch
          checked={draft.isPrep}
          onCheckedChange={(on) =>
            setDraft((d) => ({
              ...d,
              isPrep: on,
              yieldUnit: d.yieldUnit || item.unit,
              lines: on && d.lines.length === 0 ? [newPrepLine()] : d.lines,
            }))
          }
          aria-label={PREP_TEXT.toggleLabel}
          className="scale-125"
        />
      </label>

      {draft.isPrep && (
        <>
          <div>
            <span className="text-sm font-medium">{PREP_TEXT.batchMakes}</span>
            <div className="mt-1 flex items-center gap-2">
              <Input
                type="number" inputMode="decimal" min={0} step="any"
                className="w-32 tabular-nums"
                value={draft.yieldQty}
                onChange={(e) => setDraft((d) => ({ ...d, yieldQty: e.target.value }))}
                aria-label={PREP_TEXT.batchMakes}
              />
              <select
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={draft.yieldUnit}
                onChange={(e) => setDraft((d) => ({ ...d, yieldUnit: e.target.value }))}
                aria-label={PREP_TEXT.unitLabel}
              >
                {getCompatibleUnits(item.unit).map((u) => <option key={u} value={u}>{unitLabel(u)}</option>)}
              </select>
            </div>
          </div>

          <div>
            <h5 className="mb-2 text-sm font-semibold">{PREP_TEXT.ingredientsTitle}</h5>
            {draft.lines.length === 0 && <p className="mb-2 text-sm text-muted-foreground">{PREP_TEXT.noIngredients}</p>}
            <ul className="space-y-2">
              {draft.lines.map((l) => {
                const opt = byId[l.stock_item_id]
                const units = opt ? getCompatibleUnits(opt.unit) : []
                const lineCost = cost.lines[l.key]
                const problem = rowProblems.get(l.key)
                return (
                  <li key={l.key} className={cn('rounded-md border p-2', problem && 'border-destructive/60 bg-destructive/5')}>
                    <IngredientPicker
                      value={l.stock_item_id}
                      items={pickList}
                      onChange={(id) => setLine(l.key, { stock_item_id: id })}
                      formatMoney={formatCurrency}
                      autoOpen={!l.stock_item_id && !l.qty}
                    />
                    <div className="mt-2 flex items-center gap-2">
                      <Input
                        type="number" inputMode="decimal" min={0} step="any" placeholder="0"
                        className="w-28 tabular-nums"
                        value={l.qty}
                        onChange={(e) => setLine(l.key, { qty: e.target.value })}
                        aria-label={PREP_TEXT.qtyLabel}
                      />
                      <select
                        className="h-10 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-sm disabled:opacity-50"
                        value={l.unit}
                        disabled={!opt}
                        onChange={(e) => setLine(l.key, { unit: e.target.value })}
                        aria-label={PREP_TEXT.unitLabel}
                      >
                        {(units.length ? units : [l.unit]).filter(Boolean).map((u) => <option key={u} value={u}>{unitLabel(u)}</option>)}
                      </select>
                      <Button
                        type="button" variant="ghost" size="icon"
                        className="h-10 w-10 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => setDraft((d) => ({ ...d, lines: d.lines.filter((x) => x.key !== l.key) }))}
                        aria-label={PREP_TEXT.removeIngredient}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className={cn('mt-1 px-1 text-xs tabular-nums', problem ? 'text-destructive' : 'text-muted-foreground')}>
                      {problem ?? (lineCost != null ? formatCurrency(lineCost) : opt && opt.unit_cost == null ? `${opt.name}: no cost yet` : '')}
                    </p>
                  </li>
                )
              })}
            </ul>
            <Button type="button" variant="outline" className="mt-2 gap-1.5" onClick={() => setDraft((d) => ({ ...d, lines: [...d.lines, newPrepLine()] }))}>
              <Plus className="h-4 w-4" /> {PREP_TEXT.addIngredient}
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-md bg-muted/50 p-3">
              <p className="text-base font-semibold tabular-nums">{cost.perUnit != null ? formatCurrency(cost.perUnit) : '—'}</p>
              <p className="text-xs text-muted-foreground">{PREP_TEXT.costPerUnit(item.unit)}</p>
            </div>
            <div className="rounded-md bg-muted/50 p-3">
              <p className="text-base font-semibold tabular-nums">{formatCurrency(cost.batchCost)}</p>
              <p className="text-xs text-muted-foreground">{PREP_TEXT.costPerBatch}</p>
            </div>
          </div>
          {cost.uncosted > 0 && <p className="text-xs text-amber-700 dark:text-amber-300">{PREP_TEXT.uncosted(cost.uncosted)}</p>}

          <RecipeStepsEditor idPrefix={`prep-${item.id}`} steps={draft.steps} onChange={(steps) => setDraft((d) => ({ ...d, steps }))} />
        </>
      )}

      {problems.filter((p) => !p.key).map((p) => (
        dirty ? <p key={p.message} className="text-sm text-destructive">{p.message}</p> : null
      ))}
      {serverError && <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{serverError}</p>}

      {dirty && (
        <div className="flex flex-wrap gap-2">
          <Button className="flex-1" disabled={problems.length > 0 || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{PREP_TEXT.saving}</> : PREP_TEXT.save}
          </Button>
          <Button variant="outline" onClick={() => { setDraft(saved); setServerError(null) }}>
            {PREP_TEXT.undo}
          </Button>
        </div>
      )}

      {recipe && recipe.used_in.length > 0 && (
        <div className="text-sm">
          <span className="text-muted-foreground">{PREP_TEXT.usedInTitle}: </span>
          {recipe.used_in.map((p) => p.name).join(', ')}
        </div>
      )}

      {recipe?.is_prep && !dirty && (
        canProduce(recipe)
          ? <PrepProduction recipe={recipe} showToast={showToast} />
          : <p className="text-sm text-muted-foreground">{PREP_TEXT.saveRecipeFirst}</p>
      )}
    </div>
  )
}
